import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Download,
  Loader2,
  Star,
  User,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { validationService } from "../services/api";

const ValidationSuccess = () => {
  const { tamponId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [validationData, setValidationData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tamponId) {
      loadValidationData();
    }
  }, [tamponId]);

  const loadValidationData = async () => {
    try {
      setLoading(true);
      const response = await validationService.getValidationSuccess(tamponId);
      setValidationData(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      setError("Erreur lors du chargement des données de validation");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!validationData) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Validation de Tampon - ${validationData.parent.nom}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; background: #f5f5f5; }
          .container { max-width: 600px; margin: 0 auto; background: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
          .header { text-align: center; margin-bottom: 30px; }
          .success-icon { color: #10b981; font-size: 48px; margin-bottom: 10px; }
          .title { color: #1f2937; font-size: 24px; font-weight: bold; margin-bottom: 5px; }
          .subtitle { color: #6b7280; font-size: 16px; }
          .section { margin-bottom: 25px; padding: 20px; background: #f9fafb; border-radius: 8px; }
          .section-title { color: #374151; font-size: 18px; font-weight: bold; margin-bottom: 15px; display: flex; align-items: center; }
          .section-title svg { margin-right: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .info-item { }
          .info-label { color: #6b7280; font-size: 14px; font-weight: 500; margin-bottom: 5px; }
          .info-value { color: #1f2937; font-size: 16px; font-weight: 600; }
          .points { color: #f59e0b; font-size: 20px; font-weight: bold; }
          .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="success-icon">✅</div>
            <div class="title">Tampon Validé avec Succès</div>
            <div class="subtitle">Validation confirmée le ${new Date(
              validationData.tampon.date_validation
            ).toLocaleDateString("fr-FR")}</div>
          </div>
          
          <div class="section">
            <div class="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              Informations du Parent
            </div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Nom du parent</div>
                <div class="info-value">${validationData.parent.nom}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Nom de l'enfant</div>
                <div class="info-value">${
                  validationData.parent.nom_enfant
                }</div>
              </div>
              <div class="info-item">
                <div class="info-label">Classe</div>
                <div class="info-value">${
                  validationData.parent.classe_enfant
                }</div>
              </div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
              </svg>
              Détails de l'Activité
            </div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Activité</div>
                <div class="info-value">${validationData.activite.nom}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Type</div>
                <div class="info-value">${
                  validationData.activite.type_activite
                }</div>
              </div>
              <div class="info-item">
                <div class="info-label">Points obtenus</div>
                <div class="info-value points">+${
                  validationData.tampon.points_obtenus
                } pts</div>
              </div>
              <div class="info-item">
                <div class="info-label">Validé par</div>
                <div class="info-value">${validationData.validateur.nom}</div>
              </div>
            </div>
          </div>
          
          ${
            validationData.tampon.commentaire
              ? `
          <div class="section">
            <div class="section-title">Commentaire</div>
            <div class="info-value">${validationData.tampon.commentaire}</div>
          </div>
          `
              : ""
          }
          
          <div class="footer">
            <p>Validation effectuée le ${new Date(
              validationData.tampon.date_validation
            ).toLocaleString("fr-FR")}</p>
            <p>ID de validation: ${validationData.tampon.id}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `validation_tampon_${validationData.parent.nom}_${
      new Date().toISOString().split("T")[0]
    }.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Document exporté avec succès !");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-green-600" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/app/admin")}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/app/admin")}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au dashboard
          </button>

          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Tampon Validé avec Succès !
            </h1>
            <p className="text-gray-600">
              La validation a été enregistrée dans le système
            </p>
          </div>
        </div>

        {/* Informations du parent */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center mb-4">
            <User className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              Informations du parent
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du parent
              </label>
              <p className="text-gray-900 font-medium">
                {validationData?.parent.nom}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'enfant
              </label>
              <p className="text-gray-900">
                {validationData?.parent.nom_enfant}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classe
              </label>
              <p className="text-gray-900">
                {validationData?.parent.classe_enfant}
              </p>
            </div>
          </div>
        </div>

        {/* Détails de l'activité */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center mb-4">
            <Calendar className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              Détails de l'activité
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activité
              </label>
              <p className="text-gray-900 font-medium">
                {validationData?.activite.nom}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <p className="text-gray-900 capitalize">
                {validationData?.activite.type_activite}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points obtenus
              </label>
              <div className="flex items-center text-yellow-600">
                <Star className="h-4 w-4 mr-1" />
                <span className="font-bold text-lg">
                  +{validationData?.tampon.points_obtenus} pts
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validé par
              </label>
              <p className="text-gray-900">{validationData?.validateur.nom}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de validation
              </label>
              <p className="text-gray-900">
                {new Date(
                  validationData?.tampon.date_validation
                ).toLocaleString("fr-FR")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID de validation
              </label>
              <p className="text-gray-900 font-mono">
                #{validationData?.tampon.id}
              </p>
            </div>
          </div>
        </div>

        {/* Commentaire */}
        {validationData?.tampon.commentaire && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Commentaire
            </h2>
            <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
              {validationData.tampon.commentaire}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-4">
          <button
            onClick={handleExport}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter le document
          </button>

          <button
            onClick={() => navigate("/app/admin")}
            className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidationSuccess;
